// Status screen shown to users with role='pending_merchant'. Shows the
// application status (pending/approved/rejected/more_info) and admin notes.

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getMyApplication } from '../../services/businessService';
import { useAuth } from '../../contexts/AuthContext';
import '../admin/AdminLayout.css';
import './MerchantSignup.css';

const STATUS_BADGE = {
  pending: { className: 'badge-pending', label: 'Pending review' },
  approved: { className: 'badge-active', label: 'Approved' },
  rejected: { className: 'badge-inactive', label: 'Rejected' },
  more_info: { className: 'badge-pending', label: 'More info needed' },
};

const STATUS_HEADLINE = {
  pending: "We're reviewing your application",
  approved: "You're approved — welcome aboard.",
  rejected: 'Application not approved',
  more_info: 'We need a bit more information',
};

const STATUS_BODY = {
  pending: "Our team typically takes 1–2 business days. You'll get an email the moment a decision is made.",
  approved: 'Your store is now live. Head to the merchant portal to start uploading flyers and coupons.',
  rejected: 'See the note from our team below. You can reach out to support if you believe this was an error.',
  more_info: 'See the note below and reply to your signup email with the requested information.',
};

export default function MerchantPending() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { user, refresh, logout } = useAuth();

  useEffect(() => {
    let alive = true;
    getMyApplication()
      .then(d => { if (alive) setData(d.application); })
      .catch(e => {
        if (e?.response?.status === 401) navigate('/login', { replace: true });
        else if (alive) setError(e?.response?.data?.error || e.message);
      });
    return () => { alive = false; };
  }, [navigate]);

  // If the admin has since approved them, the user's role on the AuthContext
  // is stale — refresh once on mount and bounce to /merchant if upgraded.
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user?.role === 'merchant') navigate('/merchant', { replace: true });
  }, [user, navigate]);

  if (error) {
    return (
      <div className="biz-form-page">
        <div className="biz-form-shell">
          <div className="portal-error">{error}</div>
          <button type="button" className="portal-button secondary" onClick={logout}>Sign out</button>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="biz-form-page"><div className="biz-form-shell"><div className="portal-empty">Loading…</div></div></div>;
  }

  const badge = STATUS_BADGE[data.status] || STATUS_BADGE.pending;

  return (
    <div className="biz-form-page">
      <div className="biz-form-shell">
        <header>
          <div style={{ minWidth: 0 }}>
            <h1>{STATUS_HEADLINE[data.status] || 'Application status'}</h1>
            <p className="biz-form-sub">{STATUS_BODY[data.status]}</p>
          </div>
          <div style={{ display: 'flex', gap: 'var(--tudu-space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
            <span className={`badge ${badge.className}`}>{badge.label}</span>
            <button type="button" className="portal-button secondary" onClick={logout}>Sign out</button>
          </div>
        </header>

        {data.status === 'approved' && (
          <section className="biz-section" style={{ borderLeft: '3px solid var(--tudu-success)' }}>
            <Link to="/merchant" className="portal-button">Go to the merchant portal →</Link>
          </section>
        )}

        {data.admin_notes && (
          <section className="biz-section">
            <h3>Note from our team</h3>
            <p style={{ whiteSpace: 'pre-wrap', color: 'var(--tudu-text)', margin: 0 }}>{data.admin_notes}</p>
            {data.reviewed_at && (
              <p className="biz-section-hint" style={{ marginTop: 'var(--tudu-space-3)' }}>
                Reviewed {new Date(data.reviewed_at).toLocaleString()}
              </p>
            )}
          </section>
        )}

        <section className="biz-section">
          <h3>What you submitted</h3>
          <div className="portal-defs">
            <Def label="Company" value={data.company_name} />
            <Def label="Contact" value={data.contact_name} />
            <Def label="Business type" value={data.business_type} />
            <Def label="WhatsApp" value={data.store?.whatsapp_number || '—'} />
            <Def label="Phone" value={data.contact_phone || '—'} />
            <Def label="Website" value={data.website ? <a href={data.website} target="_blank" rel="noreferrer" style={{ color: 'var(--tudu-accent-amber)' }}>{data.website}</a> : '—'} />
            <Def label="Address" value={data.address || '—'} />
            <Def label="Documents" value={`${data.documents.length} uploaded`} />
          </div>
          {data.description && (
            <p style={{ marginTop: 'var(--tudu-space-4)', color: 'var(--tudu-text)' }}>
              {data.description}
            </p>
          )}
        </section>

        <section className="biz-section">
          <h3>Submitted documents</h3>
          {data.documents.length === 0 ? (
            <p className="biz-section-hint" style={{ margin: 0 }}>
              No documents attached. You can reply to your signup email to add some — it'll speed up review.
            </p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {data.documents.map(d => (
                <li key={d.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--tudu-line-1)', color: 'var(--tudu-text)' }}>
                  <strong style={{ color: 'var(--tudu-text-hi)' }}>{d.kind.replace('_', ' ')}</strong>
                  {d.filename && <span style={{ color: 'var(--tudu-text-lo)', marginLeft: 8 }}>· {d.filename}</span>}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function Def({ label, value }) {
  return (
    <div>
      <div className="portal-def-label">{label}</div>
      <div className="portal-def-value">{value}</div>
    </div>
  );
}
